import { Injectable, NgZone, ElementRef, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { DeviceService } from './tv-detect.service';

/**
 * Region represents a row or section in the TV UI
 * Each region contains focusable items (cards, buttons, etc.)
 */
export interface Region {
  id: string;
  type: 'row' | 'banner' | 'sidebar' | 'form';
  elementRef: ElementRef;
  focusableItems: ElementRef[];
}

export enum Zone {
  SIDEBAR = 'sidebar',
  CONTENT = 'content'
}

/**
 * TVNavigationService - Centralized D-pad navigation for Android TV
 *
 * This service:
 * - Intercepts D-pad arrow keys globally (outside NgZone for performance)
 * - Manages focus state via BehaviorSubject (single source of truth)
 * - Provides region-based registration API for rows/sections
 * - Handles Enter key activation
 * - Sorts regions by DOM position (dynamic row ordering)
 * - Filters out hidden/disabled elements from navigation
 * - Stops at boundaries (no wrap)
 * - Centers focused rows in viewport
 */
@Injectable({ providedIn: 'root' })
export class TVNavigationService implements OnDestroy {
  /** Registry of focusable elements by unique key */
  private registry = new Map<string, HTMLElement>();

  /** Registry of regions (rows/sections) */
  private regions = new Map<string, Region>();
  private regionLastFocusIndex = new Map<string, number>();

  /** Current focus key - single source of truth */
  private focusSubject = new BehaviorSubject<string | null>(null);

  /** Current active region ID */
  private activeRegionId = new BehaviorSubject<string | null>(null);

  /** Current focused index within the active region */
  private focusedIndex = new BehaviorSubject<number>(0);

  /** Current navigation zone (SIDEBAR or CONTENT) */
  private activeZoneState = new BehaviorSubject<Zone>(Zone.CONTENT);
  public zone$ = this.activeZoneState.asObservable();

  /** Sidebar focus index (separate from content focusedIndex) */
  private sidebarFocusIndex = new BehaviorSubject<number>(0);
  public sidebarFocusIndex$ = this.sidebarFocusIndex.asObservable();

  /** Saved content focus position for restoration after sidebar */
  private lastContentFocus: { regionId: string; index: number } | null = null;

  /** Sidebar region ID constant */
  private readonly SIDEBAR_REGION_ID = 'main-sidebar';

  /** Flag to suppress navigation when overlay (keyboard) is active */
  private _navigationSuppressed = false;

  /** Flag to skip vertical scroll in activateRegion (used by Hotstar-style preview) */
  public skipVerticalScroll = false;

  /** Signal for player components to reset auto-hide timer on D-pad interaction */
  private playerInteractionSubject = new Subject<void>();
  public playerInteraction$ = this.playerInteractionSubject.asObservable();

  /** Signal for banner components when horizontal navigation hits the boundary */
  private boundarySubject = new Subject<{regionId: string, direction: 'left' | 'right'}>();
  public boundary$ = this.boundarySubject.asObservable();

  /**
   * Signal that a region has just been registered (or re-registered).
   * Subscribers can use this to act the instant a region becomes available
   * — e.g. focus restoration on back-nav can activate the saved region as
   * soon as its row component finishes mounting, rather than poll-waiting.
   */
  private regionRegisteredSubject = new Subject<string>();
  public regionRegistered$ = this.regionRegisteredSubject.asObservable();
  private pendingRestore = false;

  setPendingRestore(value: boolean): void{
    this.pendingRestore = value;
  }

  /** Observable for components to subscribe to focus changes */
  public focus$: Observable<string | null> = this.focusSubject.asObservable();

  /** Observable for active region changes */
  public activeRegion$: Observable<string | null> = this.activeRegionId.asObservable();

  /** Observable for focused index changes */
  public focusedIndex$: Observable<number> = this.focusedIndex.asObservable();

  /** Navigation keys we handle */
  private readonly NAV_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];

  /** Cleanup subject for subscription management */
  private destroy$ = new Subject<void>();

  constructor(
    private zone: NgZone,
    private deviceService: DeviceService,
    private router: Router
  ) {
    // Only attach listener on TV platform
    if (this.deviceService.isTV()) {
      this.zone.runOutsideAngular(() => {
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
      });

      // Subscribe to route changes to clear navigation state
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.handleRouteChange();
        });
    }

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ========== PUBLIC API - REGION BASED ========== */

  /**
   * Register a region (row/section) with focusable items
   * Auto-activates first region if nothing is active
   */
  registerRegion(region: Region): void {
    const isReRegistration = this.regions.has(region.id);
    this.regions.set(region.id, region);

    // Notify subscribers that this region is now available. Fires on both
    // first registration and re-registrations — consumers that only care
    // about first registration should track that themselves.
    this.regionRegisteredSubject.next(region.id);

    // Skip auto-activation on re-registration (e.g., banner auto-slide updating
    // its focusable buttons). Without this guard the banner steals focus from
    // carousel rows every time it re-registers after an auto-slide.
    if (isReRegistration) return;

    // Auto-activate first content region with focusable items
    const activeId = this.activeRegionId.getValue();
    const activeIsSidebar = activeId === this.SIDEBAR_REGION_ID;
    const isContentRegion = region.type !== 'sidebar';

    // Activate when:
    // - Nothing active
    // - Sidebar is active and a content region registers
    //
    // We deliberately do NOT override an already-active row when a banner
    // registers. On a true first load `activeId === null`, so the banner still
    // wins (it registers ~50ms ahead of carousels — see banner.component.ts
    // ngAfterViewInit). After Back-navigation, the carousel restores focus to
    // a row first; letting the banner re-steal it then re-introduces #2675
    // ("focus jumps to Watch Now after restore").
    const shouldActivate = activeId === null
      || ((activeIsSidebar && isContentRegion) && !this.pendingRestore);

    if (shouldActivate) {
      const focusableItems = this.getFocusableItems(region);
      if (focusableItems.length > 0) {
        this.activateRegion(region.id, 0);
      }
    }
  }

  /**
   * Unregister a region (call on destroy to prevent memory leaks)
   * Falls back to another region if current focus is unregistered
   *
   * The fallback activation runs in a microtask, not synchronously. unregisterRegion
   * is typically called from ngOnDestroy, which Angular invokes during view-tree
   * cleanup inside an outer change-detection pass. Synchronously emitting a new
   * focus key would trigger subscribers (e.g. MovieThumbnailComponent) to call
   * cdr.detectChanges() re-entrantly on partially-destroyed views, throwing
   * "Cannot read properties of null (reading '_lView')" and leaving the host
   * page blank.
   */
  unregisterRegion(id: string): void {
    const wasActive = this.activeRegionId.getValue() === id;
    this.regions.delete(id);
    this.regionLastFocusIndex.delete(id);

    if (!wasActive) return;

    // Defer all reactive emissions to a microtask. unregisterRegion is typically
    // called from ngOnDestroy during NgFor cleanup, inside an outer change-detection
    // pass. Synchronously calling activateRegion (or any focus emission) would let
    // a subscriber call cdr.detectChanges() re-entrantly on partially-destroyed
    // views, throwing "Cannot read properties of null (reading '_lView')" and
    // leaving the host page blank.
    Promise.resolve().then(() => {
      // Skip if a different region became active during the microtask window
      // (e.g., a freshly-mounted page registered its first region) or the
      // unregistered region was somehow re-registered.
      if (this.activeRegionId.getValue() !== id && this.activeRegionId.getValue() !== null) return;

      const sortedIds = this.getSortedRegionIds();
      if (sortedIds.length > 0) {
        this.activateRegion(sortedIds[0], 0);
      } else {
        this.activeRegionId.next(null);
        this.focusedIndex.next(0);
        this.focusSubject.next(null);
      }
    });
  }

  /**
   * Activate a specific region and focus an item within it
   */
  activateRegion(regionId: string, index: number = 0): void {
    const region = this.regions.get(regionId);
    if (!region) return;

    const focusableItems = this.getFocusableItems(region);
    if (focusableItems.length === 0) return;

    const safeIndex = Math.min(index, focusableItems.length - 1);

    this.zone.run(() => {
      // Ensure zone matches region type — activating a content region
      // should always put us in CONTENT zone (fixes DOWN-from-banner
      // going to sidebar items when zone state is stale)
      if (region.type !== 'sidebar' && this.activeZoneState.getValue() === Zone.SIDEBAR) {
        this.activeZoneState.next(Zone.CONTENT);
      }
      this.activeRegionId.next(regionId);
      this.focusedIndex.next(safeIndex);
      this.focusItem(regionId, safeIndex);
    });

    // Vertical-only scroll: bring the row into view without any horizontal shift.
    // Skipped when skipVerticalScroll is true (Hotstar-style preview handles its own scroll).
    if (!this.skipVerticalScroll) {
      setTimeout(() => {
        if (region.type === 'banner') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const el = region.elementRef?.nativeElement
            ?? region.focusableItems?.[safeIndex]?.nativeElement;
          if (el) {
            this.scrollVerticalOnly(el);
          }
        }
      }, 50);
    }
  }

  /**
   * Get currently active region ID
   */
  getActiveRegionId(): string | null {
    return this.activeRegionId.getValue();
  }

  /**
   * Get current focused index
   */
  getFocusedIndex(): number {
    return this.focusedIndex.getValue();
  }

  /**
   * Get current navigation zone
   */
  getActiveZone(): Zone {
    return this.activeZoneState.getValue();
  }

  /**
   * Get current sidebar focus index
   */
  getSidebarFocusIndex(): number {
    return this.sidebarFocusIndex.getValue();
  }

  /**
   * Check if there's a saved content focus position (for sidebar/focus-memory priority)
   * Returns true when user just switched from sidebar and there's a valid region to restore.
   * Browse components use this to determine focus restoration priority:
   * - If true: let TVNavigationService handle restoration (user just left sidebar)
   * - If false: restore from TVFocusMemoryService (returning from detail page)
   */
  hasLastContentFocus(): boolean {
    return this.lastContentFocus !== null && this.regions.has(this.lastContentFocus.regionId);
  }

  /**
   * Suppress navigation key handling (used by keyboard overlay)
   * When suppressed, handleKeyDown is a no-op so the keyboard component
   * can process D-pad keys without interference.
   */
  suppressNavigation(): void {
    this._navigationSuppressed = true;
  }

  /**
   * Resume navigation key handling after overlay closes
   */
  resumeNavigation(): void {
    this._navigationSuppressed = false;
  }

  /**
   * Get the type of the currently active region
   */
  getActiveRegionType(): string | null {
    const regionId = this.activeRegionId.getValue();
    if (!regionId) return null;
    return this.regions.get(regionId)?.type || null;
  }

  /**
   * Switch to sidebar zone, saving current content position
   * Called when LEFT is pressed at index 0 in content
   */
  switchToSidebar(): void {
    // Save current content position before switching
    const regionId = this.activeRegionId.getValue();
    const index = this.focusedIndex.getValue();
    if (regionId && this.regions.get(regionId)?.type !== 'sidebar') {
      this.lastContentFocus = { regionId, index };
    }

    this.zone.run(() => {
      this.activeZoneState.next(Zone.SIDEBAR);
      // Focus first sidebar item (or last focused)
      this.focusSidebarItem(this.sidebarFocusIndex.getValue());
    });
  }

  /**
   * Switch to content zone, restoring last content position
   * Called when RIGHT is pressed in sidebar
   */
  switchToContent(): void {
    this.zone.run(() => {
      this.activeZoneState.next(Zone.CONTENT);

      // Restore last content position if valid
      if (this.lastContentFocus && this.regions.has(this.lastContentFocus.regionId)) {
        this.activateRegion(this.lastContentFocus.regionId, this.lastContentFocus.index);
      } else {
        // Fallback to first content region
        const contentRegions = this.getSortedRegionIds().filter(id =>
          this.regions.get(id)?.type !== 'sidebar'
        );
        if (contentRegions.length > 0) {
          this.activateRegion(contentRegions[0], 0);
        }
      }
    });
  }

  /**
   * Handle route changes - clear all non-sidebar regions and reset focus state
   * Sidebar persists across routes, content regions are page-specific
   * First registerRegion() call from new page will auto-focus
   */
  private handleRouteChange(): void {
    this.zone.run(() => {
      // Collect all non-sidebar region IDs
      const regionsToRemove: string[] = [];
      this.regions.forEach((region, id) => {
        if (region.type !== 'sidebar') {
          regionsToRemove.push(id);
        }
      });

      // Delete collected content regions
      regionsToRemove.forEach(id => this.regions.delete(id));

      // Clear old page context
      this.lastContentFocus = null;

      // Reset to content zone
      this.activeZoneState.next(Zone.CONTENT);

      // Clear focus state (new page will auto-focus via registerRegion)
      this.activeRegionId.next(null);
      this.focusedIndex.next(0);
      this.focusSubject.next(null);

    });
  }

  /* ========== PUBLIC API - ELEMENT BASED ========== */

  /**
   * Register a focusable element with a unique key
   * Auto-focuses first registered element if nothing is focused
   */
  register(key: string, element: HTMLElement): void {
    this.registry.set(key, element);

    // Auto-focus first element if nothing is currently focused
    if (this.focusSubject.getValue() === null) {
      this.setFocus(key);
    }
  }

  /**
   * Unregister an element (call on destroy to prevent memory leaks)
   * Falls back to another element if current focus is unregistered
   */
  unregister(key: string): void {
    this.registry.delete(key);

    // If we just removed the focused element, fallback to first available
    if (this.focusSubject.getValue() === key) {
      const firstKey = this.registry.keys().next().value;
      this.setFocus(firstKey ?? null);
    }
  }

  /**
   * Programmatically set focus to a specific key
   */
  setFocus(key: string | null): void {
    this.zone.run(() => {
      this.focusSubject.next(key);
    });
  }

  /**
   * Get the currently focused key
   */
  getCurrentFocusKey(): string | null {
    return this.focusSubject.getValue();
  }

  /**
   * Get the currently focused element
   */
  getCurrentFocusElement(): HTMLElement | undefined {
    const key = this.focusSubject.getValue();
    return key ? this.registry.get(key) : undefined;
  }

  /**
   * Activate (click) the currently focused element
   * Handles sidebar Enter activation separately
   */
  activateCurrentFocus(): void {
    const zone = this.activeZoneState.getValue();

    // SIDE-04: Handle sidebar Enter - activate menu item
    if (zone === Zone.SIDEBAR) {
      const sidebarRegion = this.regions.get(this.SIDEBAR_REGION_ID);
      if (sidebarRegion) {
        const items = this.getFocusableItems(sidebarRegion);
        const index = this.sidebarFocusIndex.getValue();
        const item = items[index];
        if (item) {
          this.zone.run(() => {
            item.nativeElement.click();
          });
          // Clear lastContentFocus on section navigation
          this.lastContentFocus = null;
        }
      }
      return;
    }

    // Handle content Enter - activate card/button
    const regionId = this.activeRegionId.getValue();
    if (regionId) {
      const region = this.regions.get(regionId);
      if (region) {
        const focusableItems = this.getFocusableItems(region);
        const index = this.focusedIndex.getValue();
        const item = focusableItems[index];
        if (item) {
          this.zone.run(() => {
            this.activateElement(item.nativeElement);
          });
          return;
        }
      }
    }

    // Fall back to element-based focus
    const element = this.getCurrentFocusElement();
    if (element) {
      this.zone.run(() => {
        this.activateElement(element);
      });
    }
  }

  /**
   * Activate an element by dispatching Enter keydown then click.
   * Dispatching keydown first ensures Angular (keydown.enter) bindings fire
   * (e.g. form inputs that open the TV keyboard).
   * The click handles buttons, links, and other clickable elements.
   * Uses activating flag to prevent re-entrant calls from the synthetic keydown
   * bubbling back to handleKeyDown.
   */
  private activating = false;
  private activateElement(el: HTMLElement): void {
    if (this.activating) return;
    this.activating = true;
    el.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'Enter', code: 'Enter', keyCode: 13, bubbles: true, cancelable: true
    }));
    el.click();
    this.activating = false;
  }

  /* ========== PRIVATE METHODS - NAVIGATION ========== */

  /**
   * Global keydown handler - runs outside Angular zone for performance
   */
  private handleKeyDown(event: KeyboardEvent): void {
    // Skip when navigation is suppressed (keyboard overlay active)
    if (this._navigationSuppressed) {
      return;
    }

    const key = event.key;

    // Only handle navigation keys
    if (!this.NAV_KEYS.includes(key)) {
      return;
    }

    // Prevent default browser behavior (scrolling, etc.)
    event.preventDefault();

    if (key === 'Enter') {
      this.activateCurrentFocus();
      return;
    }

    // Handle arrow key navigation
    switch (key) {
      case 'ArrowUp':
        this.moveVertical('up');
        break;
      case 'ArrowDown':
        this.moveVertical('down');
        break;
      case 'ArrowLeft':
        this.moveHorizontal('left');
        break;
      case 'ArrowRight':
        this.moveHorizontal('right');
        break;
    }

    // Signal player interaction for auto-hide timer reset
    const activeRegionId = this.activeRegionId.getValue();
    if (activeRegionId?.startsWith('player-')) {
      this.zone.run(() => {
        this.playerInteractionSubject.next();
      });
    }
  }

  /**
   * Move focus horizontally within the current row
   * Handles sidebar zone transitions (LEFT at index 0 → sidebar, RIGHT in sidebar → content)
   */
  private moveHorizontal(direction: 'left' | 'right'): void {
    const zone = this.activeZoneState.getValue();

    // Handle sidebar zone
    if (zone === Zone.SIDEBAR) {
      if (direction === 'right') {
        // SIDE-02: RIGHT from sidebar returns to content
        this.switchToContent();
      }
      // LEFT in sidebar: do nothing (at edge of screen)
      return;
    }

    // Handle content zone
    const currentRegionId = this.activeRegionId.getValue();
    if (!currentRegionId) return;

    const region = this.regions.get(currentRegionId);
    if (!region) return;

    // Forms: ArrowLeft triggers sidebar (or emits boundary for cross-panel navigation).
    // ArrowRight emits boundary for cross-panel navigation (e.g. login QR panel).
    if (region.type === 'form') {
      if (direction === 'left') {
        if (this.regions.has(this.SIDEBAR_REGION_ID)) {
          this.switchToSidebar();
        } else {
          this.boundarySubject.next({ regionId: currentRegionId, direction });
        }
      } else if (direction === 'right') {
        this.boundarySubject.next({ regionId: currentRegionId, direction });
      }
      return;
    }

    const focusableItems = this.getFocusableItems(region);
    if (focusableItems.length === 0) return;

    const currentIdx = this.focusedIndex.getValue();
    let newIndex = currentIdx;

    if (direction === 'left') {
      if (currentIdx === 0) {
        // Banner regions: emit boundary so the banner can navigate to previous slide.
        // Only switch to sidebar if the banner is already on the first slide.
        if (region.type === 'banner') {
          this.boundarySubject.next({ regionId: currentRegionId, direction });
          return;
        }
        // SIDE-01: At first item, check if sidebar exists and switch to it
        if (this.regions.has(this.SIDEBAR_REGION_ID)) {
          this.switchToSidebar();
          return;
        }
      }
      // Move left within row
      newIndex = Math.max(0, currentIdx - 1);
    } else {
      // Stop at right boundary
      newIndex = Math.min(focusableItems.length - 1, currentIdx + 1);
    }

    if (newIndex !== currentIdx) {
      this.zone.run(() => {
        this.focusedIndex.next(newIndex);
        this.focusItem(currentRegionId, newIndex);
      });
      // Do NOT scroll the page when moving horizontally.
      // Each carousel component handles its own horizontal sliding via translateX.
      // Page-level scrollIntoView causes the entire screen to shift (the bug).
    } else if (region.type === 'banner') {
      // At boundary of a banner region — notify so the banner can advance slides
      this.boundarySubject.next({ regionId: currentRegionId, direction });
    }
  }

  /**
   * Move focus vertically between rows
   * Handles sidebar zone navigation separately (UP/DOWN in sidebar moves between menu items)
   */
  private moveVertical(direction: 'up' | 'down'): void {
    const zone = this.activeZoneState.getValue();

    // SIDE-03: Handle sidebar zone - UP/DOWN navigates sidebar items
    if (zone === Zone.SIDEBAR) {
      this.moveSidebarVertical(direction);
      return;
    }

    // Handle form regions - navigate between form fields, or exit to next region
    const currentRegionId = this.activeRegionId.getValue();
    const currentRegion = this.regions.get(currentRegionId || '');
    if (currentRegion?.type === 'form') {
      const handled = this.moveFormVertical(direction, currentRegion);
      if (handled) return;
      // At form boundary — fall through to region navigation so focus can move
      // to the previous/next region (e.g., episodes list -> tabs above)
    }

    // Handle content zone - navigate between rows
    const regionIds = this.getSortedRegionIds().filter(id =>
      this.regions.get(id)?.type !== 'sidebar'
    );
    if (regionIds.length === 0) return;

    const currentId = this.activeRegionId.getValue() || regionIds[0];
    const currentIdx = regionIds.indexOf(currentId);

    let nextIdx: number;
    if (direction === 'up') {
      nextIdx = Math.max(0, currentIdx - 1);
    } else {
      nextIdx = Math.min(regionIds.length - 1, currentIdx + 1);
    }

    if (nextIdx !== currentIdx) {
      // Save current focus index before leaving this region
      this.regionLastFocusIndex.set(currentId, this.focusedIndex.getValue());
      const nextRegionId = regionIds[nextIdx];
      // Restore last focused index for the target region, or default to 0
      const savedIndex = this.regionLastFocusIndex.get(nextRegionId) ?? 0;
      this.activateRegion(nextRegionId, savedIndex);
    }
  }

  /* ========== PRIVATE METHODS - HELPERS ========== */

  /**
   * Focus a specific sidebar item by index
   */
  private focusSidebarItem(index: number): void {
    const sidebarRegion = this.regions.get(this.SIDEBAR_REGION_ID);
    if (!sidebarRegion) return;

    const items = this.getFocusableItems(sidebarRegion);
    if (items.length === 0) return;

    const safeIndex = Math.min(index, items.length - 1);
    this.sidebarFocusIndex.next(safeIndex);
    this.focusSubject.next(`${this.SIDEBAR_REGION_ID}:${safeIndex}`);

    // Scroll sidebar item into view
    items[safeIndex]?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }

  /**
   * Scroll an element into view vertically only, preventing any horizontal scroll.
   * This is critical for TV: each carousel row handles its own horizontal scrolling
   * via translateX, so we must never allow the browser to shift containers horizontally.
   */
  private scrollVerticalOnly(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 40; // px buffer above/below

    if (rect.top < margin) {
      // Element is above viewport — scroll up
      window.scrollBy({ top: rect.top - margin, behavior: 'smooth' });
    } else if (rect.bottom > viewportHeight - margin) {
      // Element is below viewport — scroll down
      window.scrollBy({ top: rect.bottom - viewportHeight + margin, behavior: 'smooth' });
    }
    // No horizontal scrolling ever.
  }

  /**
   * Move focus within sidebar (UP/DOWN)
   */
  private moveSidebarVertical(direction: 'up' | 'down'): void {
    const sidebarRegion = this.regions.get(this.SIDEBAR_REGION_ID);
    if (!sidebarRegion) return;

    const items = this.getFocusableItems(sidebarRegion);
    if (items.length === 0) return;

    const currentIdx = this.sidebarFocusIndex.getValue();
    let newIndex: number;

    if (direction === 'up') {
      newIndex = Math.max(0, currentIdx - 1);
    } else {
      newIndex = Math.min(items.length - 1, currentIdx + 1);
    }

    if (newIndex !== currentIdx) {
      this.zone.run(() => {
        this.focusSidebarItem(newIndex);
      });
    }
  }

  /**
   * Move focus vertically within a form region (between fields)
   * Forms use tab-order navigation, not spatial
   */
  private moveFormVertical(direction: 'up' | 'down', region: Region): boolean {
    const focusableItems = this.getFocusableItems(region);
    if (focusableItems.length === 0) return false;

    const currentIdx = this.focusedIndex.getValue();
    let newIndex: number;

    if (direction === 'up') {
      if (currentIdx === 0) return false; // At boundary, let caller handle
      newIndex = currentIdx - 1;
    } else {
      if (currentIdx >= focusableItems.length - 1) return false; // At boundary
      newIndex = currentIdx + 1;
    }

    this.zone.run(() => {
      this.focusedIndex.next(newIndex);
      this.focusItem(region.id, newIndex);
    });

    // Scroll form field into view (vertical only)
    if (focusableItems[newIndex]?.nativeElement) {
      this.scrollVerticalOnly(focusableItems[newIndex].nativeElement);
    }
    return true;
  }

  /**
   * Get sorted region IDs with priority: sidebar excluded -> banners first -> rows by DOM position
   * Filters out regions with no focusable items
   */
  private getSortedRegionIds(): string[] {
    const regionEntries = Array.from(this.regions.entries())
      .filter(([id, region]) => this.getFocusableItems(region).length > 0);

    regionEntries.sort((a, b) => {
      const regionA = a[1];
      const regionB = b[1];

      // Sidebar always excluded from content navigation (handled separately)
      if (regionA.type === 'sidebar') return 1;
      if (regionB.type === 'sidebar') return -1;

      // Form regions participate in vertical navigation (sorted by DOM position)
      // They can be navigated away from at their boundaries

      // Banners come first (hero elements)
      if (regionA.type === 'banner' && regionB.type !== 'banner') return -1;
      if (regionA.type !== 'banner' && regionB.type === 'banner') return 1;

      // Same type or both 'row' - sort by vertical DOM position
      const elA = regionA.elementRef?.nativeElement;
      const elB = regionB.elementRef?.nativeElement;
      if (!elA || !elB) return 0;
      const rectA = elA.getBoundingClientRect();
      const rectB = elB.getBoundingClientRect();
      const topA = rectA.top + window.scrollY;
      const topB = rectB.top + window.scrollY;
      return topA - topB;
    });

    return regionEntries.map(entry => entry[0]);
  }

  /**
   * Check if an element is focusable (visible, not disabled)
   */
  private isElementFocusable(el: HTMLElement): boolean {
    // Use checkVisibility if available (Chrome 105+)
    if (typeof (el as any).checkVisibility === 'function') {
      if (!(el as any).checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) {
        return false;
      }
    } else {
      // Fallback: offsetParent null means display:none (unless fixed position)
      if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') {
        return false;
      }
      if (el.offsetWidth === 0 || el.offsetHeight === 0) {
        return false;
      }
    }

    // Check disabled state
    if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') {
      return false;
    }

    // Negative tabindex means not focusable
    const tabindex = el.getAttribute('tabindex');
    if (tabindex !== null && parseInt(tabindex, 10) < 0) {
      return false;
    }

    return true;
  }

  /**
   * Get focusable items from a region (filtered for visibility)
   */
  private getFocusableItems(region: Region): ElementRef[] {
    return region.focusableItems.filter(item =>
      item?.nativeElement && this.isElementFocusable(item.nativeElement)
    );
  }

  /**
   * Focus a specific item within a region
   * Updates the focus subject with a composite key
   */
  private focusItem(regionId: string, index: number): void {
    const focusKey = `${regionId}:${index}`;
    this.focusSubject.next(focusKey);
  }

}
